import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@byscott-io/corebyscott";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";

function StatCard({ title, value, change, icon }) {
  const isPositive = change >= 0;
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
          {change !== undefined && (
            <p className={`text-sm mt-1 ${isPositive ? "text-green-600" : "text-red-600"}`}>
              {isPositive ? "+" : ""}{change}% from last period
            </p>
          )}
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { currentContainer } = useAuth();

  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard-stats", currentContainer?.id],
    queryFn: api.getDashboardStats,
    enabled: !!currentContainer,
  });

  if (!currentContainer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading account...</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-200 rounded-lg h-32" />
          ))}
        </div>
      </div>
    );
  }

  // Mock data for now
  const chartData = [
    { date: "Mon", events: 400, values: 24 },
    { date: "Tue", events: 300, values: 13 },
    { date: "Wed", events: 520, values: 38 },
    { date: "Thu", events: 278, values: 19 },
    { date: "Fri", events: 189, values: 48 },
    { date: "Sat", events: 239, values: 38 },
    { date: "Sun", events: 349, values: 43 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500">Analytics overview for {currentContainer?.name || "your account"}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Apps"
          value={stats?.total_apps?.toLocaleString() || "0"}
          icon="📱"
        />
        <StatCard
          title="Total Events"
          value={stats?.total_events?.toLocaleString() || "0"}
          change={stats?.events_change}
          icon="⚡"
        />
        <StatCard
          title="Events Today"
          value={stats?.events_today?.toLocaleString() || "0"}
          icon="📅"
        />
        <StatCard
          title="Metric Values"
          value={stats?.total_values?.toLocaleString() || "0"}
          icon="📈"
        />
        <StatCard
          title="Active Metrics"
          value={stats?.active_metrics?.toLocaleString() || "0"}
          icon="📊"
        />
      </div>

      {/* Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Events This Week</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats?.chart_data || chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="events"
                stroke="#6366f1"
                fill="#6366f1"
                fillOpacity={0.2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Events</h2>
        {stats?.recent_events?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_events.map((event, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <span className="font-medium text-gray-900">{event.event_name}</span>
                  <span className="text-gray-500 text-sm ml-2">
                    {new Date(event.occurred_at).toLocaleString()}
                  </span>
                </div>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                  {event.properties ? Object.keys(event.properties).length : 0} props
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No events recorded yet</p>
        )}
      </div>
    </div>
  );
}
